import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ArrivalsBoardService } from './arrivals-board.service';
import { ArrivalsSmsDialogComponent } from './arrivals-sms-dialog/arrivals-sms-dialog.component';
import { AlertService } from '@app/_services';

@Component({
    selector: 'app-arrivals-board',
    templateUrl: './arrivals-board.component.html',
    styleUrls: ['./arrivals-board.component.scss'],
    providers: [{ provide: MAT_DIALOG_DATA, useValue: {} }],
    standalone: false
})
export class ArrivalsBoardComponent implements OnInit, OnDestroy {

    arrivals: any[] = [];
    loading = false;
    lastRefreshed: Date | null = null;
    autoRefreshEnabled = true;
    reportTriggering = false;
    reportMessage: string | null = null;

    private refreshInterval: any = null;
    private readonly REFRESH_MS = 60000; // 60 seconds

    // Summary counts
    get totalToday(): number { return this.arrivals.length; }
    get smsSent(): number { return this.arrivals.filter(a => a._smsSent).length; }
    get smsPending(): number { return this.arrivals.filter(a => !a._smsSent && !a._held).length; }
    get flagged(): number { return this.arrivals.filter(a => a._held).length; }

    // Today's date string for the API filter
    get todayStr(): string {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    constructor(
        private arrivalsBoardService: ArrivalsBoardService,
        private alertService: AlertService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.loadArrivals();
        this.startAutoRefresh();
    }

    ngOnDestroy(): void {
        this.stopAutoRefresh();
    }

    loadArrivals(): void {
        this.loading = true;
        this.arrivalsBoardService.getTodayArrivals(this.todayStr).subscribe({
            next: (res: any) => {
                const raw: any[] = res?.data?.reservations || [];
                const enriched = raw.map(r => this.enrichRow(r));
                // After loading rows, fetch SMS status from DB to pre-populate sent state
                this.arrivalsBoardService.getSmsStatus(this.todayStr).subscribe({
                    next: (statusRes: any) => {
                        const sentMap: Record<string, any> = statusRes?.sentByReservationId || {};
                        this.arrivals = enriched.map(row => {
                            const resId = String(row.reservationId || '');
                            if (resId && sentMap[resId]) {
                                return {
                                    ...row,
                                    _smsSent: true,
                                    _lastSmsSentAt: sentMap[resId].sentAt || null
                                };
                            }
                            return row;
                        });
                        this.lastRefreshed = new Date();
                        this.loading = false;
                    },
                    error: () => {
                        // SMS status fetch failed — fall back to unenriched rows
                        this.arrivals = enriched;
                        this.lastRefreshed = new Date();
                        this.loading = false;
                    }
                });
            },
            error: (err: any) => {
                this.alertService.error('Failed to load arrivals: ' + (err?.statusText || 'Unknown error'));
                this.loading = false;
            }
        });
    }

    /**
     * Enrich a raw reservation row with derived display fields.
     * Door code validity is determined from available data.
     * SMS sent state starts false; loadArrivals() overwrites it from the DB.
     */
    private enrichRow(r: any): any {
        const doorCode = r.accessCodeFrontDoor || '';
        const doorCodeValid = doorCode.length > 0 && doorCode !== '????' && doorCode.toLowerCase() !== '[see wiki]';
        const doorCodeStatus = !doorCode ? 'missing' : (!doorCodeValid ? 'invalid' : 'valid');

        return {
            ...r,
            _doorCodeValid: doorCodeValid,
            _doorCodeStatus: doorCodeStatus,
            _smsSent: false,
            _lastSmsSentAt: null,
            _held: false,
            _holdReason: null
        };
    }

    getRowColorClass(row: any): string {
        if (row._smsSent) return 'row-green';
        if (row._held || !row._doorCodeValid) return 'row-red';
        return 'row-amber';
    }

    getDoorCodeBadgeClass(row: any): string {
        if (row._doorCodeStatus === 'valid') return 'badge-green';
        if (row._doorCodeStatus === 'invalid') return 'badge-red';
        return 'badge-grey';
    }

    getDoorCodeLabel(row: any): string {
        if (row._doorCodeStatus === 'valid') return 'Valid';
        if (row._doorCodeStatus === 'invalid') return 'Invalid';
        return 'Missing';
    }

    getSmsStatusClass(row: any): string {
        if (row._smsSent) return 'badge-green';
        if (row._held) return 'badge-red';
        return 'badge-amber';
    }

    getSmsStatusLabel(row: any): string {
        if (row._smsSent) return 'Sent';
        if (row._held) return 'Held';
        return 'Pending';
    }

    toggleHold(row: any): void {
        row._held = !row._held;
        row._holdReason = row._held ? 'Manually held by operator' : null;
    }

    openSmsDialog(row: any): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '520px';
        dialogConfig.data = {
            title: 'Send Arrival SMS',
            reservation: row
        };

        const dialogRef = this.dialog.open(ArrivalsSmsDialogComponent, dialogConfig);
        dialogRef.componentInstance.smsSent.subscribe((sentAt: string | null) => {
            row._smsSent = true;
            row._held = false;
            row._lastSmsSentAt = sentAt || null;
            dialogRef.close();
        });
    }

    triggerDailyReport(): void {
        if (!confirm('Trigger the Daily Arrivals Dashboard report email now?')) return;
        this.reportTriggering = true;
        this.reportMessage = null;
        this.arrivalsBoardService.triggerDailyReport().subscribe({
            next: (res: any) => {
                this.reportTriggering = false;
                this.reportMessage = res?.message || 'Report sent to all recipients.';
            },
            error: (err: any) => {
                this.reportTriggering = false;
                this.reportMessage = 'Error: ' + (err?.error?.error || err?.statusText || 'Unknown error');
            }
        });
    }

    startAutoRefresh(): void {
        if (this.refreshInterval) return;
        this.refreshInterval = setInterval(() => {
            if (this.autoRefreshEnabled) {
                this.loadArrivals();
            }
        }, this.REFRESH_MS);
    }

    stopAutoRefresh(): void {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    toggleAutoRefresh(): void {
        this.autoRefreshEnabled = !this.autoRefreshEnabled;
    }

    manualRefresh(): void {
        this.loadArrivals();
    }

    getPartySize(row: any): string {
        const adults = row.numAdults || 0;
        const children = row.numChildren || 0;
        if (children > 0) return `${adults}A / ${children}C`;
        return `${adults}A`;
    }

    formatCheckInTime(row: any): string {
        if (!row.checkInDate) return '—';
        return row.checkInDate;
    }
}
